/**
 * @name useReceiptPrinter
 * @author Keven Adan Bezerra | kevenadanb
 * @description Hook para impressão de recibos e comandas via QZ Tray
 * @returns {ReceiptPrinterHookReturn} Métodos para impressão de recibos
 * @package src/hooks/useReceiptPrinter.ts
 */

import { useCallback } from "react";
import { useAlert } from "./useAlert";
import { useQZTray } from "./useQZTray";

/**
 * Remove caracteres especiais de uma string para impressão
 */
const removeSpecialChars = (text: string): string => {
  return text
    .normalize("NFD") // Normaliza caracteres acentuados
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[çÇ]/g, "c") // Replace ç/Ç
    .replace(/[^\w\s.-]/g, "") // Remove outros caracteres especiais, mantendo letras, números, espaços, pontos e hífens
    .trim();
};

// Tipos para dados do recibo
interface ItemRecibo {
  nome: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
}

interface DadosRecibo {
  numero_pedido: string;
  data_hora: string;
  aluno_nome: string;
  aluno_matricula: string;
  itens: ItemRecibo[];
  valor_total: number;
  operador_nome: string;
  organizacao_nome: string;
}

interface ReceiptPrinterHookReturn {
  imprimirRecibo: (dados: DadosRecibo) => Promise<boolean>;
  imprimirComanda: (dados: DadosRecibo) => Promise<boolean>;
  imprimirTesteRecibo: () => Promise<boolean>;
  detectarImpressora: () => Promise<string | null>; // Nova função
}

/**
 * Hook para impressão de recibos e comandas
 */
export const useReceiptPrinter = (): ReceiptPrinterHookReturn => {
  const {
    selectedPrinter,
    isConnected,
    connectToQZTray,
    getPrinters,
    setDefaultPrinter,
  } = useQZTray();
  const { showError, showSuccess } = useAlert();

  /**
   * Formata valor monetário
   */
  const formatMoney = useCallback((value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }, []);

  /**
   * Gera conteúdo do recibo
   */
  const gerarConteudoRecibo = useCallback(
    (dados: DadosRecibo): string[] => {
      const conteudo: string[] = [];

      // Cabeçalho
      conteudo.push("\x1B\x40"); // Reset printer
      conteudo.push("\x1B\x61\x01"); // Center alignment
      conteudo.push("\x1B\x21\x30"); // Double size
      conteudo.push(`${dados.organizacao_nome}\n`);
      conteudo.push("\x1B\x21\x00"); // Normal size
      conteudo.push("Sagrada Familia\n");
      conteudo.push("RECIBO DE COMPRA\n");
      conteudo.push("\x1B\x61\x00"); // Left alignment
      conteudo.push("\n");

      // Linha separadora
      conteudo.push("================================\n");

      // Dados do pedido
      conteudo.push(`Pedido: ${dados.numero_pedido}\n`);
      conteudo.push(`Data/Hora: ${dados.data_hora}\n`);
      conteudo.push(`Aluno: ${dados.aluno_nome}\n`);
      conteudo.push(`Matricula: ${dados.aluno_matricula}\n`);
      conteudo.push(`Operador: ${dados.operador_nome}\n`);
      conteudo.push("================================\n");

      // Itens
      conteudo.push("ITENS:\n");
      dados.itens.forEach((item) => {
        const nomeItem = removeSpecialChars(item.nome);
        const nomeFormatado =
          nomeItem.length > 20 ? nomeItem.substring(0, 17) + "..." : nomeItem;

        conteudo.push(`${item.quantidade}x ${nomeFormatado}\n`);
        conteudo.push(
          `   ${formatMoney(item.valor_unitario)} = ${formatMoney(item.valor_total)}\n`,
        );
      });

      conteudo.push("================================\n");

      // Total
      conteudo.push("\x1B\x21\x10"); // Double width
      conteudo.push(`TOTAL: ${formatMoney(dados.valor_total)}\n`);
      conteudo.push("\x1B\x21\x00"); // Normal size

      conteudo.push("================================\n");
      conteudo.push("\n");
      conteudo.push("Obrigado pela preferencia!\n");
      conteudo.push("Tenha um excelente dia!\n");
      conteudo.push("\n\n\n");

      // Cortar papel (se suportado)
      conteudo.push("\x1D\x56\x00");

      return conteudo;
    },
    [formatMoney],
  );

  /**
   * Gera conteúdo da comanda (versão simplificada)
   */
  const gerarConteudoComanda = useCallback(
    (dados: DadosRecibo): string[] => {
      const conteudo: string[] = [];

      // Cabeçalho
      conteudo.push("\x1B\x40"); // Reset printer
      conteudo.push("\x1B\x61\x01"); // Center alignment
      conteudo.push("\x1B\x21\x30"); // Double size
      conteudo.push("COMANDA\n");
      conteudo.push("\x1B\x21\x00"); // Normal size
      conteudo.push("\x1B\x61\x00"); // Left alignment
      conteudo.push("\n");

      // Dados essenciais
      conteudo.push(`Pedido: ${dados.numero_pedido}\n`);
      conteudo.push(`Aluno: ${dados.aluno_nome}\n`);
      conteudo.push(`Hora: ${dados.data_hora.split(" ")[1]}\n`);
      conteudo.push("------------------------\n");

      // Itens simplificados
      dados.itens.forEach((item) => {
        const nomeItem = removeSpecialChars(item.nome);
        conteudo.push(`${item.quantidade}x ${nomeItem}\n`);
      });

      conteudo.push("------------------------\n");
      conteudo.push(`Total: ${formatMoney(dados.valor_total)}\n`);
      conteudo.push("\n\n");

      // Cortar papel
      conteudo.push("\x1D\x56\x00");

      return conteudo;
    },
    [formatMoney],
  );

  /**
   * Detecta e seleciona automaticamente uma impressora disponível
   */
  const autoDetectPrinter = useCallback(async (): Promise<string | null> => {
    try {
      console.log("🔍 Detectando impressora automaticamente...");

      // Buscar impressoras disponíveis
      const availablePrinters = await getPrinters();

      if (availablePrinters.length === 0) {
        console.warn("⚠️ Nenhuma impressora encontrada");
        return null;
      }

      console.log(
        "📋 Impressoras disponíveis:",
        availablePrinters.map((p: any) => p.name),
      );

      // 1. Buscar impressora padrão do sistema
      try {
        const defaultPrinter = await window.qz.printers.getDefault();
        if (defaultPrinter) {
          console.log("✅ Impressora padrão detectada:", defaultPrinter);
          setDefaultPrinter(defaultPrinter);
          return defaultPrinter;
        }
      } catch (defaultError) {
        console.log("ℹ️ Erro ao detectar impressora padrão:", defaultError);
      }

      // 2. Buscar "Microsoft Print to PDF" (útil para desenvolvimento)
      const pdfPrinter = availablePrinters.find(
        (printer: any) =>
          printer.name.toLowerCase().includes("microsoft print to pdf") ||
          printer.name.toLowerCase().includes("print to pdf") ||
          printer.name.toLowerCase().includes("pdf"),
      );

      if (pdfPrinter) {
        console.log("✅ Microsoft Print to PDF detectado:", pdfPrinter.name);
        setDefaultPrinter(pdfPrinter.name);
        return pdfPrinter.name;
      }

      // 3. Usar primeira impressora disponível
      const firstPrinter = availablePrinters[0];
      console.log(
        "✅ Usando primeira impressora disponível:",
        firstPrinter.name,
      );
      setDefaultPrinter(firstPrinter.name);
      return firstPrinter.name;
    } catch (error) {
      console.error("❌ Erro ao detectar impressora:", error);
      return null;
    }
  }, [getPrinters, setDefaultPrinter]);

  /**
   * Executa impressão com detecção automática de impressora
   */
  const executarImpressao = useCallback(
    async (conteudo: string[], tipo: string): Promise<boolean> => {
      // 🛡️ Identificador único para esta impressão
      const impressaoId = `${tipo}_${Date.now()}`;
      console.log(`🖨️ Executando impressão de ${tipo} (ID: ${impressaoId})...`);

      // 🛡️ Controle global para evitar impressões simultâneas do mesmo tipo
      const chaveControle = `impressao_unica_${tipo}`;
      if ((window as any)[chaveControle]) {
        console.warn(`🚫 Impressão de ${tipo} já em andamento, ignorando`);
        return false;
      }

      // Marcar como em execução
      (window as any)[chaveControle] = true;

      try {
        console.log("📊 Estado atual:", {
          impressaoId,
          selectedPrinter,
          isConnected,
          qzAvailable: !!window.qz,
          websocketActive: window.qz?.websocket?.isActive?.(),
        });

        // 🎯 Auto-detectar impressora se não estiver selecionada
        let printerToUse = selectedPrinter;
        if (!printerToUse) {
          console.log(
            "🔍 Nenhuma impressora selecionada, detectando automaticamente...",
          );
          printerToUse = await autoDetectPrinter();

          if (!printerToUse) {
            console.error("❌ Nenhuma impressora disponível");
            showError(
              "Nenhuma impressora disponível. Verifique se há impressoras instaladas.",
            );
            return false;
          }
        }

        console.log("🖨️ Usando impressora:", printerToUse);

        if (!window.qz) {
          console.warn("❌ QZ Tray não está disponível");
          showError("QZ Tray não está disponível");
          return false;
        }

        // Verifica conexão
        if (!isConnected || !window.qz.websocket.isActive()) {
          console.log("🔄 Tentando reconectar ao QZ Tray...");
          const connected = await connectToQZTray();
          if (!connected) {
            console.warn("❌ Falha ao conectar com QZ Tray");
            return false;
          }
        }

        console.log(`📄 Criando configuração para impressora: ${printerToUse}`);

        // Configuração da impressora
        const config = window.qz.configs.create(printerToUse, {
          encoding: "CP850", // Codificação para caracteres especiais
        });

        console.log(`📤 Enviando ${tipo} para impressão...`);

        // Enviar para impressão
        await window.qz.print(config, conteudo);

        console.log(`✅ ${tipo} enviado com sucesso para ${printerToUse}!`);
        showSuccess(`${tipo} enviado para ${printerToUse} com sucesso`);
        return true;
      } catch (error) {
        console.error(`❌ Erro ao imprimir ${tipo.toLowerCase()}:`, error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";
        showError(`Erro ao imprimir ${tipo.toLowerCase()}: ${errorMessage}`);
        return false;
      } finally {
        // Limpar o controle após um delay
        setTimeout(() => {
          delete (window as any)[chaveControle];
          console.log(`🧹 Limpeza do controle de impressão para: ${tipo}`);
        }, 1500);
      }
    },
    [selectedPrinter, isConnected, connectToQZTray, showError, showSuccess],
  );

  /**
   * Imprime recibo completo
   */
  const imprimirRecibo = useCallback(
    async (dados: DadosRecibo): Promise<boolean> => {
      const conteudo = gerarConteudoRecibo(dados);
      return executarImpressao(conteudo, "Recibo");
    },
    [gerarConteudoRecibo, executarImpressao],
  );

  /**
   * Imprime comanda simplificada
   */
  const imprimirComanda = useCallback(
    async (dados: DadosRecibo): Promise<boolean> => {
      const conteudo = gerarConteudoComanda(dados);
      return executarImpressao(conteudo, "Comanda");
    },
    [gerarConteudoComanda, executarImpressao],
  );

  /**
   * Imprime recibo de teste
   */
  const imprimirTesteRecibo = useCallback(async (): Promise<boolean> => {
    const dadosTeste: DadosRecibo = {
      numero_pedido: "TEST-001",
      data_hora: new Date().toLocaleString("pt-BR"),
      aluno_nome: "João da Silva",
      aluno_matricula: "123456",
      operador_nome: "Operador Teste",
      organizacao_nome: "Escola Exemplo",
      valor_total: 15.5,
      itens: [
        {
          nome: "Lanche Natural",
          quantidade: 1,
          valor_unitario: 8.0,
          valor_total: 8.0,
        },
        {
          nome: "Suco de Laranja",
          quantidade: 1,
          valor_unitario: 4.5,
          valor_total: 4.5,
        },
        {
          nome: "Barra de Cereal",
          quantidade: 1,
          valor_unitario: 3.0,
          valor_total: 3.0,
        },
      ],
    };

    return imprimirRecibo(dadosTeste);
  }, [imprimirRecibo]);

  return {
    imprimirRecibo,
    imprimirComanda,
    imprimirTesteRecibo,
    detectarImpressora: autoDetectPrinter, // Expor função de detecção
  };
};
